import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Save, Archive, ArchiveRestore, GitMerge, FileText, Calendar,
    AlertTriangle, Mail, Phone, MapPin, Briefcase, IdCard, Stethoscope,
    Receipt, Plus, Download,
} from 'lucide-react';
import { apiService, Patient, PatientDetail, InvoiceDocument, InvoiceType } from '../../services/apiService';
import { statusLabel, statusTone, typeLabel, fmtMoney } from './billing/invoiceHelpers';
import { useToast } from '../../contexts/ToastContext';
import PageHeader from './ui/PageHeader';
import StatusPill from './ui/StatusPill';
import TreatmentNotesSection from './notes/TreatmentNotesSection';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';
const sectionClass = 'rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5 mb-4';

const fmtDateDe = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    confirmed: 'success', completed: 'success',
    pending: 'warning', scheduled: 'neutral',
    cancelled: 'danger', no_show: 'danger',
};

const PatientDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [data, setData] = useState<PatientDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Editable form state — initialized from data, sent on Save
    const [form, setForm] = useState<Partial<Patient>>({});
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    // Invoices for this patient — loaded separately so the patient detail
    // endpoint stays focused on the master record.
    const [invoices, setInvoices] = useState<InvoiceDocument[]>([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);

    const loadInvoices = async (patientId: number) => {
        try {
            setInvoicesLoading(true);
            const res = await apiService.listInvoices({ patient_id: patientId });
            setInvoices(res.data.data || []);
        } catch {
            setInvoices([]);
        } finally {
            setInvoicesLoading(false);
        }
    };

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const res = await apiService.getPatient(parseInt(id));
            setData(res.data.data);
            setForm({});
            setDirty(false);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Patient konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => {
        if (id) loadInvoices(parseInt(id));
        // eslint-disable-next-line
    }, [id]);

    const startNewInvoice = (type: InvoiceType) => {
        if (!id) return;
        navigate(`/admin/invoices/new?type=${type}&patient=${id}`);
    };

    const openInvoicePdf = async (inv: InvoiceDocument) => {
        try {
            const blob = await apiService.fetchInvoicePdf(inv.id, { preview: !inv.finalized_at });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'PDF konnte nicht geladen werden', 'error');
        }
    };

    const update = (field: keyof Patient, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setDirty(true);
    };

    const value = (field: keyof Patient): any => {
        if (form[field] !== undefined) return form[field];
        return data?.[field] ?? '';
    };

    const handleSave = async () => {
        if (!data || !dirty) return;
        try {
            setSaving(true);
            await apiService.updatePatient(data.id, form);
            addToast('Stammdaten gespeichert', 'success');
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || e?.response?.data?.error || 'Speichern fehlgeschlagen', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async () => {
        if (!data) return;
        if (!window.confirm(`Patient ${data.full_name} archivieren? Termine bleiben erhalten.`)) return;
        try {
            await apiService.archivePatient(data.id);
            addToast('Patient archiviert', 'success');
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Archivieren fehlgeschlagen', 'error');
        }
    };

    const handleUnarchive = async () => {
        if (!data) return;
        try {
            await apiService.unarchivePatient(data.id);
            addToast('Patient reaktiviert', 'success');
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Reaktivieren fehlgeschlagen', 'error');
        }
    };

    const handleMerge = async (targetId: number, targetName: string) => {
        if (!data) return;
        if (!window.confirm(
            `Diesen Patienten (${data.full_name}, ${data.patient_number}) IN den anderen (${targetName}) verschmelzen?\n\nDieser Datensatz wird archiviert, alle Termine wandern um.`
        )) return;
        try {
            const res = await apiService.mergePatients(data.id, targetId);
            const moved = res.data.data?.moved;
            addToast(`Merge ok — ${moved?.appointments || 0} Termine umgehängt, Anamnese: ${moved?.intake || 'keine'}`, 'success');
            navigate(`/admin/patients/${targetId}`);
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Merge fehlgeschlagen', 'error');
        }
    };

    if (loading) {
        return <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>;
    }
    if (error || !data) {
        return (
            <div>
                <Link to="/admin/patients" className="inline-flex items-center gap-1 text-sm text-brand-primary mb-4">
                    <ArrowLeft className="h-4 w-4" /> Zurück zur Liste
                </Link>
                <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800">
                    {error || 'Patient nicht gefunden'}
                </div>
            </div>
        );
    }

    return (
        <div>
            <Link to="/admin/patients" className="inline-flex items-center gap-1 text-sm text-brand-primary hover:text-brand-primary-hover mb-4 transition-colors">
                <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Zurück zur Liste
            </Link>

            <PageHeader
                eyebrow={data.patient_number}
                title={data.full_name || 'Patient'}
                description={
                    [
                        data.email,
                        data.phone_primary,
                        data.date_of_birth ? `*${fmtDateDe(data.date_of_birth)}` : null,
                    ].filter(Boolean).join(' · ') || undefined
                }
                actions={
                    <>
                        {data.archived ? (
                            <StatusPill tone="danger" dot>Archiviert</StatusPill>
                        ) : (
                            <StatusPill tone="success" dot>Aktiv</StatusPill>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={!dirty || saving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors disabled:opacity-40 cursor-pointer"
                        >
                            <Save className="h-3.5 w-3.5" strokeWidth={2} />
                            {saving ? 'Speichere…' : 'Speichern'}
                        </button>
                        {data.archived ? (
                            <button onClick={handleUnarchive} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-text bg-brand-surface-2 hover:bg-brand-border rounded-md cursor-pointer">
                                <ArchiveRestore className="h-3.5 w-3.5" strokeWidth={1.75} /> Reaktivieren
                            </button>
                        ) : (
                            <button onClick={handleArchive} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/10 rounded-md cursor-pointer">
                                <Archive className="h-3.5 w-3.5" strokeWidth={1.75} /> Archivieren
                            </button>
                        )}
                    </>
                }
            />

            {/* Stammdaten */}
            <div className={sectionClass}>
                <h3 className="text-sm font-semibold text-brand-text mb-3 flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-brand-primary" strokeWidth={1.75} /> Stammdaten
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2">
                        <label className={labelClass}>Voller Name</label>
                        <input value={value('full_name')} onChange={e => update('full_name', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Sprache</label>
                        <select value={value('preferred_language') || 'es'} onChange={e => update('preferred_language', e.target.value)} className={inputClass}>
                            <option value="de">Deutsch</option>
                            <option value="en">English</option>
                            <option value="es">Español</option>
                            <option value="ru">Русский</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>E-Mail</label>
                        <input type="email" value={value('email') || ''} onChange={e => update('email', e.target.value || null)} className={inputClass} placeholder="optional bei Walk-In" />
                    </div>
                    <div>
                        <label className={labelClass}>Telefon (primär)</label>
                        <input value={value('phone_primary') || ''} onChange={e => update('phone_primary', e.target.value || null)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Telefon (sekundär)</label>
                        <input value={value('phone_secondary') || ''} onChange={e => update('phone_secondary', e.target.value || null)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Geburtsdatum (YYYY-MM-DD)</label>
                        <input type="date" value={value('date_of_birth') || ''} onChange={e => update('date_of_birth', e.target.value || null)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Geburtsort</label>
                        <input value={value('place_of_birth')} onChange={e => update('place_of_birth', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>NIE</label>
                        <input value={value('nie')} onChange={e => update('nie', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Beruf</label>
                        <input value={value('occupation')} onChange={e => update('occupation', e.target.value)} className={inputClass} />
                    </div>
                    <div className="lg:col-span-2">
                        <label className={labelClass}>Straße</label>
                        <input value={value('street')} onChange={e => update('street', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>PLZ</label>
                        <input value={value('postal_code')} onChange={e => update('postal_code', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Stadt</label>
                        <input value={value('city')} onChange={e => update('city', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Land (ISO, z.B. ES)</label>
                        <input value={value('country')} onChange={e => update('country', e.target.value.toUpperCase())} maxLength={2} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Versicherung</label>
                        <input value={value('insurance') || ''} onChange={e => update('insurance', e.target.value || null)} className={inputClass} />
                    </div>
                </div>

                <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mt-6 mb-2 flex items-center gap-2">
                    <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.75} /> Hausarzt
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className={labelClass}>Name</label>
                        <input value={value('doctor_name')} onChange={e => update('doctor_name', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Adresse</label>
                        <input value={value('doctor_address')} onChange={e => update('doctor_address', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>Telefon</label>
                        <input value={value('doctor_phone')} onChange={e => update('doctor_phone', e.target.value)} className={inputClass} />
                    </div>
                </div>

                <div className="mt-4">
                    <label className={labelClass}>Interne Notizen</label>
                    <textarea
                        rows={3}
                        value={value('notes')}
                        onChange={e => update('notes', e.target.value)}
                        className={inputClass}
                        placeholder="Sichtbar nur im Admin — z.B. besondere Hinweise zum Patienten"
                    />
                </div>
            </div>

            {/* Duplikat-Verdacht */}
            {data.duplicate_suspects.length > 0 && (
                <div className={`${sectionClass} border-amber-200 bg-amber-50/40`}>
                    <h3 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.75} />
                        Mögliche Duplikate ({data.duplicate_suspects.length})
                    </h3>
                    <p className="text-xs text-amber-800/80 mb-3">
                        Diese Patienten teilen Namen oder Telefonnummer. Prüfe und merge ggf., damit alle Termine an einem Datensatz hängen.
                    </p>
                    <ul className="divide-y divide-amber-200/60">
                        {data.duplicate_suspects.map(s => (
                            <li key={s.id} className="py-2 flex items-center justify-between gap-3">
                                <Link to={`/admin/patients/${s.id}`} className="min-w-0 hover:text-brand-primary">
                                    <div className="text-sm font-medium text-brand-text truncate">{s.full_name}</div>
                                    <div className="text-xs text-brand-text-muted truncate">
                                        {s.patient_number} · {s.email || s.phone_primary || '—'}
                                    </div>
                                </Link>
                                <button
                                    onClick={() => handleMerge(s.id, s.full_name)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-text bg-white hover:bg-amber-100 border border-amber-300 rounded transition-colors cursor-pointer"
                                    title={`Diesen Patienten in ${s.full_name} verschmelzen`}
                                >
                                    <GitMerge className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    In diesen mergen
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Termine */}
            <div className={sectionClass}>
                <h3 className="text-sm font-semibold text-brand-text mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
                    Termine ({data.appointments.length})
                </h3>
                {data.appointments.length === 0 ? (
                    <p className="text-sm text-brand-text-muted">Noch keine Termine.</p>
                ) : (
                    <ul className="divide-y divide-brand-border">
                        {data.appointments.map(a => (
                            <li key={a.id} className="py-2.5 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-brand-text tabular-nums">
                                        {fmtDateDe(a.appointment_date)} · {a.start_time}
                                    </div>
                                    <div className="text-xs text-brand-text-muted truncate">
                                        {(a as any).procedure_name || '—'} · {(a as any).provider_name || '—'}
                                    </div>
                                </div>
                                <StatusPill tone={STATUS_TONE[a.status] || 'neutral'} dot>{a.status}</StatusPill>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Behandlungsverlauf */}
            <TreatmentNotesSection
                patientId={data.id}
                appointments={data.appointments}
                patientName={(data as any).full_name || (data as any).name || `Patient #${data.id}`}
                patientPreferredLocale={(data as any).preferred_language}
            />

            {/* Belege */}
            <div className={sectionClass}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-brand-text flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
                        Belege ({invoices.length})
                    </h3>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => startNewInvoice('offer')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded transition-colors cursor-pointer"
                        >
                            <Plus className="h-3 w-3" strokeWidth={2} />
                            Angebot
                        </button>
                        <button
                            type="button"
                            onClick={() => startNewInvoice('invoice')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded transition-colors cursor-pointer"
                        >
                            <Plus className="h-3 w-3" strokeWidth={2} />
                            Rechnung
                        </button>
                    </div>
                </div>
                {invoicesLoading ? (
                    <p className="text-sm text-brand-text-muted">Lade…</p>
                ) : invoices.length === 0 ? (
                    <p className="text-sm text-brand-text-muted">Noch keine Belege.</p>
                ) : (
                    <ul className="divide-y divide-brand-border">
                        {invoices.map(inv => (
                            <li key={inv.id} className="py-2.5 flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                                    className="flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
                                >
                                    <div className="text-sm font-medium text-brand-text tabular-nums">
                                        {inv.number || <span className="italic text-brand-text-muted">Entwurf</span>}
                                        <span className="ml-2 text-xs font-normal text-brand-text-muted">{typeLabel(inv.type)}</span>
                                    </div>
                                    <div className="text-xs text-brand-text-muted tabular-nums">
                                        {fmtDateDe(inv.issue_date)}
                                        {inv.type === 'invoice' && inv.due_date && <> · fällig {fmtDateDe(inv.due_date)}</>}
                                        {inv.appointment_ids && inv.appointment_ids.length > 0 && <> · Termin #{inv.appointment_ids[0]}</>}
                                    </div>
                                </button>
                                <div className="flex items-center gap-2 shrink-0">
                                    <div className="text-sm font-medium text-brand-text tabular-nums">{fmtMoney(inv.total_cents, inv.currency || 'EUR')}</div>
                                    <StatusPill tone={statusTone(inv.status)} dot>{statusLabel(inv.status)}</StatusPill>
                                    {(inv.finalized_at || inv.status === 'draft') && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); openInvoicePdf(inv); }}
                                            className="p-1 text-brand-primary hover:bg-brand-accent/10 rounded cursor-pointer"
                                            title="PDF öffnen"
                                        >
                                            <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Anamnese */}
            <div className={sectionClass}>
                <h3 className="text-sm font-semibold text-brand-text mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-brand-primary" strokeWidth={1.75} /> Anamnese
                </h3>
                {data.intake_form ? (
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <StatusPill tone="success" dot>Ausgefüllt</StatusPill>
                            {data.intake_form.submitted_at && (
                                <span className="text-xs text-brand-text-muted">
                                    eingereicht {new Date(data.intake_form.submitted_at).toLocaleString('de-DE')}
                                </span>
                            )}
                        </div>
                        {data.intake_pdf_available && data.intake_form.appointment_id && (
                            <Link
                                to={`/admin/intake`}
                                state={{ openAppointmentId: data.intake_form.appointment_id }}
                                className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors"
                            >
                                <FileText className="h-3.5 w-3.5" strokeWidth={1.75} />
                                Zum Bogen + PDF
                            </Link>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-brand-text-muted">
                        Noch kein Anamnesebogen eingereicht.
                    </p>
                )}
            </div>
        </div>
    );
};

export default PatientDetailPage;
